import { ManagedLeadNoteFailure } from '../../services/api/managed-leads/managedLeads';
import {
  classifyTassoQuintoError,
  getTassoQuintoErrorCode,
  getTassoQuintoErrorFields,
} from '../../services/api/tasso-quinto';

/**
 * Copy shared by every managed lead mutation. These three failures are about
 * the session and the lead, not about what was being asked of it, so an update
 * and a Drop request have no reason to word them differently.
 */
export const MANAGED_LEAD_SESSION_EXPIRED_MESSAGE =
  'La sessione è scaduta. Accedi di nuovo.';

export const MANAGED_LEAD_AUTH_UNAVAILABLE_MESSAGE =
  'Il servizio di autenticazione non è momentaneamente disponibile. I dati inseriti sono stati conservati.';

export const MANAGED_LEAD_DEAL_NOT_FOUND_MESSAGE =
  'La lead richiesta non è più disponibile.';

/**
 * Same outage as `MANAGED_LEAD_AUTH_UNAVAILABLE_MESSAGE`, read from a page that
 * has no form: there is nothing typed to reassure the operator about.
 */
export const MANAGED_LEAD_AUTH_UNAVAILABLE_READ_MESSAGE =
  'Il servizio di autenticazione non è momentaneamente disponibile. Riprova tra qualche istante.';

/**
 * An operator without a HubSpot owner has no Deal of their own. It is a state,
 * not a failure: the session stays valid and the page has nothing to retry.
 */
export const MANAGED_LEAD_OWNER_MISSING_MESSAGE =
  'Non risulta un owner HubSpot associato al tuo utente: non ci sono lead da mostrare.';

/**
 * The envelope names the request section too (`body.note`), because the server
 * validates params and body together. The operator only cares about the field.
 */
const fieldName = (field: string) => field.split('.').pop() ?? field;

/**
 * The fields an `invalid_request` rejected: the bare names, to put a message
 * under the right input, and the labels, to name them in the alert. They always
 * travel together, so they are read once.
 */
export interface ManagedLeadRejectedFields {
  names: string[];
  labels: string[];
}

export const readManagedLeadRejectedFields = (
  error: unknown,
  labels: Record<string, string>
): ManagedLeadRejectedFields => {
  const names = getTassoQuintoErrorFields(error).map(fieldName);
  return { names, labels: names.map((name) => labels[name] ?? name) };
};

/**
 * How one write path names itself in the failures every write path shares. A
 * rejected payload, a refused scope, a vanished lead and a spent idempotency
 * key mean the same thing whoever sent them, so only the noun changes and the
 * decisions those codes drive are taken once.
 */
export interface ManagedLeadWriteAct {
  /** The generic, retryable failure: what could not be saved. */
  failed: string;
  /** Opens the `invalid_request` sentence, e.g. "L'aggiornamento non è valido". */
  invalid: string;
  /** The refusal to write this lead at all. */
  unauthorized: string;
  /** Closes the idempotency conflict: what pressing Riprova will send. */
  resend: string;
}

/**
 * What a failed write means for the form it was sent from. Every flag drives
 * one decision, so a component never has to re-read the envelope code to know
 * what to do next.
 */
export interface ManagedLeadWriteFailure {
  message: string;
  /** Pressing Riprova with the same content can still succeed. */
  retryable: boolean;
  /** The stored idempotency key is burnt: a retry has to mint a new one. */
  renewsIdempotencyKey: boolean;
  /** The metadata driving the form are stale and must be re-read. */
  refetchesDetail: boolean;
  /** The session, not the write, failed: send the user to the login page. */
  sessionExpired: boolean;
  /**
   * The bare semantic names the server rejected, in the order it named them.
   * A path that owns a field of its own reads them to answer for it.
   */
  rejectedFields: string[];
  /**
   * Message to show under each rejected input on screen, keyed by semantic
   * name. The HubSpot property behind it never reaches here, so there is
   * nothing internal to leak into the form.
   */
  fieldMessages: Record<string, string>;
}

const REJECTED_FIELD_MESSAGE = 'Il valore non è stato accettato.';

/**
 * How the operator hears the parts of one payload named. `form` is what has an
 * input on screen and `other` what the payload carries without rendering it:
 * only a name on screen has somewhere to put a message of its own, and both are
 * named in the sentence.
 */
export interface ManagedLeadWriteFieldLabels {
  form: Record<string, string>;
  other: Record<string, string>;
}

const baseFailure = (act: ManagedLeadWriteAct): ManagedLeadWriteFailure => ({
  message: act.failed,
  retryable: true,
  renewsIdempotencyKey: false,
  refetchesDetail: false,
  sessionExpired: false,
  rejectedFields: [],
  fieldMessages: {},
});

const invalidRequestFailure = (
  error: unknown,
  act: ManagedLeadWriteAct,
  fieldLabels: ManagedLeadWriteFieldLabels
): ManagedLeadWriteFailure => {
  const { names, labels } = readManagedLeadRejectedFields(error, {
    ...fieldLabels.other,
    ...fieldLabels.form,
  });

  return {
    ...baseFailure(act),
    message: labels.length
      ? `${act.invalid}: controlla ${labels.join(', ')}.`
      : `${act.invalid}: controlla i dati inseriti.`,
    // A rejected payload never reached HubSpot, but the key is already spent
    // for it: a corrected retry has to travel with a new one.
    retryable: false,
    renewsIdempotencyKey: true,
    rejectedFields: names,
    // Only the fields on screen: a name the form is not showing has no input to
    // sit under, and the alert already names it.
    fieldMessages: names
      .filter((name) => name in fieldLabels.form)
      .reduce<Record<string, string>>(
        (messages, name) => ({ ...messages, [name]: REJECTED_FIELD_MESSAGE }),
        {}
      ),
  };
};

/**
 * The answer for a response no `code` case matches. It reuses the read path's
 * classifier so that an uncoded or newly minted response is translated by the
 * same rules everywhere, instead of defaulting to a Riprova that a refused or
 * vanished lead can only answer the same way again.
 */
const classifiedFailure = (
  error: unknown,
  act: ManagedLeadWriteAct
): ManagedLeadWriteFailure => {
  const base = baseFailure(act);
  switch (classifyTassoQuintoError(error)) {
    case 'session_expired':
      return {
        ...base,
        message: MANAGED_LEAD_SESSION_EXPIRED_MESSAGE,
        retryable: false,
        sessionExpired: true,
      };
    case 'auth_unavailable':
      return { ...base, message: MANAGED_LEAD_AUTH_UNAVAILABLE_MESSAGE };
    case 'unauthorized':
      return { ...base, message: act.unauthorized, retryable: false };
    case 'not_found':
      return {
        ...base,
        message: MANAGED_LEAD_DEAL_NOT_FOUND_MESSAGE,
        retryable: false,
      };
    case 'owner_missing':
      return {
        ...base,
        message: MANAGED_LEAD_OWNER_MISSING_MESSAGE,
        retryable: false,
      };
    default:
      return base;
  }
};

/**
 * Translates the envelope codes every managed lead write can receive, reacting
 * to the stable `code` and never to its message. A path with codes of its own
 * answers for those first and delegates the rest here, so the shared failures
 * are not translated twice with two wordings.
 */
export const describeManagedLeadWriteFailure = (
  error: unknown,
  act: ManagedLeadWriteAct,
  fieldLabels: ManagedLeadWriteFieldLabels = { form: {}, other: {} }
): ManagedLeadWriteFailure => {
  const base = baseFailure(act);
  // The session codes are already the read path's business: reusing its
  // classifier keeps one definition of "the session is gone".
  if (classifyTassoQuintoError(error) === 'session_expired') {
    return classifiedFailure(error, act);
  }

  switch (getTassoQuintoErrorCode(error)) {
    case 'authentication_unavailable':
      return { ...base, message: MANAGED_LEAD_AUTH_UNAVAILABLE_MESSAGE };
    case 'insufficient_scope':
      return { ...base, message: act.unauthorized, retryable: false };
    case 'deal_not_found':
      return {
        ...base,
        message: MANAGED_LEAD_DEAL_NOT_FOUND_MESSAGE,
        retryable: false,
      };
    case 'idempotency_conflict':
      return {
        ...base,
        message: `Il tentativo precedente aveva un contenuto diverso. ${act.resend}`,
        renewsIdempotencyKey: true,
      };
    case 'idempotency_in_progress':
      return {
        ...base,
        message:
          'Un invio con lo stesso contenuto è ancora in corso. Attendi qualche istante e riprova.',
      };
    case 'invalid_idempotency_key':
      return { ...base, renewsIdempotencyKey: true };
    case 'invalid_request':
      return invalidRequestFailure(error, act, fieldLabels);
    default:
      return classifiedFailure(error, act);
  }
};

/**
 * What a `200` means for the form that sent it. A `200` is not automatically
 * good news on either write path: what the write applied is irreversible and
 * the Nota that documents it is not, so a write whose Nota failed is announced
 * as an outstanding step and never as a success.
 */
export interface ManagedLeadWriteOutcome {
  severity: 'success' | 'warning';
  message: string;
  /** Only the Nota is still missing: the same key and payload resume it. */
  retryable: boolean;
}

/**
 * Whether the Nota a write left outstanding can still be completed. Both write
 * paths read it the same way, and a server that says nothing about the failure
 * is taken to leave the retry open rather than to close it.
 */
export const isManagedLeadNoteRetryable = (note: {
  failure?: ManagedLeadNoteFailure;
}) => note.failure?.retryable !== false;
